import { cn } from '@invent/wl-ui-kit-next';
import {
  Button,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle
} from '@invent/wl-ui-kit-next/ui';
import { ArrowRight, Compass, RotateCcw, X } from 'lucide-react';
import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { StatusChip } from '@/design';
import { roleNames, useAuth, useCan, type Can, type IUser } from '@/domains/auth';
import { DEMO_ROLES, DEMO_ROLE_LABEL, type DemoRole } from '../roles';
import { useDemo } from '../state/demo-state';
import {
  DEMO_ACTS,
  OUT_OF_THE_DEMO,
  STEPS_IN_ORDER,
  STEP_AUDIENCE_LABEL,
  type DemoAct,
  type IDemoStep
} from '../steps';

/**
 * The demo guide (the two-portals design, §8): a sheet that slides out from a floating button or
 * Ctrl/⌘+., and closes for screenshots. It lists the walkthrough's twenty-three steps by act, each
 * with its `live` / `mock` pill and a jump straight to its screen, in whichever portal it is — **the
 * one thing, besides the two boundary pages, allowed to cross from one portal to the other**, and
 * it crosses by address alone: it imports neither zone, and each zone's guard still decides what
 * its address shows.
 *
 * It also holds "View as role", which changes only the mock screens and the rail's mock entries and
 * says so; "Reset demo", which puts every mock back to its fixtures; and the one line naming what
 * the demo leaves out.
 */
export function DemoGuide() {
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && !event.altKey && event.key === '.') {
        event.preventDefault();
        setOpen((current) => !current);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-keyshortcuts="Control+Period Meta+Period"
        data-demo-guide-button=""
        className="fixed right-4 bottom-20 z-40 inline-flex h-10 items-center gap-2 rounded-full border border-dashed border-[var(--veritant-yellow-edge)] bg-[color-mix(in_srgb,var(--color-yellow)_45%,white)] px-4 text-sm font-medium text-[var(--color-midnight-blue)] shadow-md outline-none hover:bg-[var(--color-yellow)] focus-visible:ring-2 focus-visible:ring-[var(--color-royal-blue)]"
      >
        <Compass className="size-4" aria-hidden />
        Demo guide
      </button>
      <SheetContent side="right" className="w-[min(30rem,calc(100vw-2rem))] gap-0 p-0">
        <GuideBody onJump={() => setOpen(false)} />
      </SheetContent>
    </Sheet>
  );
}

function GuideBody({ onJump }: { onJump: () => void }) {
  const { state, dispatch } = useDemo();
  const { user } = useAuth();
  const can = useCan();
  const { pathname } = useLocation();
  const [resetSaid, setResetSaid] = React.useState(false);
  const viewId = React.useId();

  const acts = (Object.keys(DEMO_ACTS).map(Number) as DemoAct[]).map((act) => ({
    act,
    steps: STEPS_IN_ORDER.filter((step) => step.act === act)
  }));

  const reset = () => {
    dispatch({ type: 'reset' });
    setResetSaid(true);
  };

  return (
    <div className="flex h-full min-h-0 flex-col text-[0.875rem] text-[var(--color-midnight-blue)]">
      <div className="flex items-start justify-between gap-3 border-b border-border px-5 pt-5 pb-4">
        <div className="grid gap-1">
          <SheetTitle className="text-lg leading-tight font-medium text-[var(--color-royal-blue)]">
            Demo guide
          </SheetTitle>
          <SheetDescription className="not-sr-only text-[0.8125rem] leading-snug text-muted-foreground">
            Twenty-three steps in five acts, across both portals. A live step reads real data; a mock
            step reads the demo’s invented data and says so on its screen.
          </SheetDescription>
        </div>
        <SheetClose asChild>
          <Button type="button" variant="ghost" size="icon" aria-label="Close the guide" className="shrink-0">
            <X className="size-4" aria-hidden />
          </Button>
        </SheetClose>
      </div>

      <div className="grid gap-2 border-b border-border px-5 py-4">
        <label htmlFor={viewId} className="font-medium">
          View as role
        </label>
        <select
          id={viewId}
          value={state.viewAs ?? ''}
          onChange={(event) =>
            dispatch({ type: 'view-as', role: (event.target.value || null) as Nullable<DemoRole> })
          }
          className="h-9 rounded-md border border-input bg-white px-2 outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <option value="">As signed in{user ? ` (${sessionWords(user)})` : ''}</option>
          {DEMO_ROLES.map((role) => (
            <option key={role} value={role}>
              {DEMO_ROLE_LABEL[role]}
            </option>
          ))}
        </select>
        <p className="text-[0.8125rem] leading-snug text-muted-foreground">
          Changes only the mock screens and the Team Portal’s mock entries, by the demo’s own role
          matrix. Live screens and live entries follow the Team Portal roles you signed in with.
        </p>
        <div className="flex flex-wrap items-center gap-3 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={reset} className="gap-1.5">
            <RotateCcw className="size-3.5" aria-hidden />
            Reset demo
          </Button>
          <span role="status" className="text-[0.8125rem] text-muted-foreground">
            {resetSaid ? 'Every mock screen is back to its fixtures.' : ''}
          </span>
        </div>
      </div>

      <nav aria-label="Demo steps" className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        {acts.map(({ act, steps }) => (
          <section key={act} aria-labelledby={`demo-act-${act}`} className="mb-5 last:mb-0">
            <h3 id={`demo-act-${act}`} className="mb-2 text-[0.8125rem] font-medium text-muted-foreground">
              Act {act} — {DEMO_ACTS[act]}
            </h3>
            <ol className="grid gap-1.5">
              {steps.map((step) => (
                <StepEntry
                  key={step.key}
                  step={step}
                  current={pathname === step.to}
                  mismatch={audienceMismatch(step, user, can)}
                  onJump={onJump}
                />
              ))}
            </ol>
          </section>
        ))}
      </nav>

      <p className="border-t border-border px-5 py-3 text-[0.8125rem] text-muted-foreground">{OUT_OF_THE_DEMO}</p>
    </div>
  );
}

function StepEntry({
  step,
  current,
  mismatch,
  onJump
}: {
  step: IDemoStep;
  current: boolean;
  mismatch: Nullable<string>;
  onJump: () => void;
}) {
  return (
    <li
      data-demo-step={step.number}
      aria-current={current ? 'step' : undefined}
      className={cn(
        'grid gap-1 rounded-md border px-3 py-2',
        current ? 'border-[var(--color-teal)] bg-[color-mix(in_srgb,var(--color-turquoise)_8%,white)]' : 'border-border bg-white'
      )}
    >
      <div className="flex items-start gap-2">
        <span className="w-5 shrink-0 pt-px text-right font-medium tabular-nums text-muted-foreground">
          {step.number}
        </span>
        <span className="min-w-0 flex-1 font-medium">{step.title}</span>
        <StatusChip tone={step.status === 'live' ? 'success' : 'mock'}>{step.status}</StatusChip>
      </div>
      <p className="pl-7 text-[0.8125rem] leading-snug text-muted-foreground">{step.what}</p>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pl-7">
        <Link
          to={step.to}
          onClick={onJump}
          aria-label={`Go to step ${step.number}: ${step.title}`}
          className="inline-flex items-center gap-1 text-[0.8125rem] font-medium text-[var(--color-sapphire)] underline-offset-4 hover:underline"
        >
          Go <ArrowRight className="size-3.5" aria-hidden />
        </Link>
        {step.also?.map((link) => (
          <Link
            key={link.to}
            to={link.to}
            onClick={onJump}
            className="text-[0.8125rem] text-[var(--color-sapphire)] underline-offset-4 hover:underline"
          >
            {link.label}
          </Link>
        ))}
        <span className="text-[0.75rem] text-muted-foreground">{STEP_AUDIENCE_LABEL[step.audience]}</span>
      </div>
      {mismatch && <p className="pl-7 text-[0.75rem] text-[var(--color-deep-magenta)]">{mismatch}</p>}
    </li>
  );
}

/** What stands between the viewer's real session and a step's screen — said, never enforced: the
 *  zone's own guard answers the address whatever the guide thinks. The account's kind decides the
 *  portal, and a Team Portal step's `needs` the permission, as its route's `AdminRoute` does. */
function audienceMismatch(step: IDemoStep, user: Nullable<IUser>, can: Can): Nullable<string> {
  switch (step.audience) {
    case 'signed-out':
      return user ? 'Sign out first to walk this step as a newcomer.' : null;
    case 'advisor':
      if (!user) return 'Sign in as an advisor.';
      return user.kind === 'advisor'
        ? null
        : 'Your account is a Team Portal one; an Advisor Portal account opens this.';
    case 'team':
      if (!user) return 'Sign in with a Team Portal account.';
      if (user.kind === 'advisor') {
        return 'Your account is an Advisor Portal one; a Team Portal account opens this.';
      }
      return step.needs && !can(step.needs.permission)
        ? `Your Team Portal roles do not include ${step.needs.what}.`
        : null;
  }
}

/** The signed-in session in the guide's words: an advisor, or a Team Portal account's roles. */
function sessionWords(user: IUser): string {
  if (user.kind === 'advisor') return 'advisor';
  return roleNames(user.roles) ?? 'no role';
}
